import { ReportIcon } from './SessionReportSessionView';
import React, { useEffect, useRef, useState } from 'react';
import {
    describeNotices, describeProminentNotices, formatKst, getNoticeCount, PROMINENT_NOTICE_CODES, SessionReport,
} from '../../features/sessionReport';
import { ExplanationToggle, formatReportNumber, screenReportText, useReportDisclosure } from './sessionReportPresentation';

/** 보조 설정과 설명은 머리의 더보기에서 열고 닫는다. */
export const SessionReportOptions = ({ controls, report, totalRows = 0 }: {
    controls?: React.ReactNode;
    report?: SessionReport;
    totalRows?: number;
}) => {
    const menu = useRef<HTMLDetailsElement>(null);
    useEffect(() => {
        const closeOutside = (event: MouseEvent) => {
            if (menu.current?.open && !menu.current.contains(event.target as Node)) menu.current.open = false;
        };
        const closeEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && menu.current?.open) {
                menu.current.open = false;
                menu.current.querySelector('summary')?.focus();
            }
        };
        document.addEventListener('click', closeOutside);
        document.addEventListener('keydown', closeEscape);
        return () => {
            document.removeEventListener('click', closeOutside);
            document.removeEventListener('keydown', closeEscape);
        };
    }, []);
    const [showBasis, setShowBasis] = useReportDisclosure('basis');
    return <details ref={menu} className="relative shrink-0">
        <summary className="flex h-[44px] w-[44px] cursor-pointer list-none items-center justify-center rounded-[10px] border border-solid border-[#E1E6ED] bg-white text-[#202630] hover:bg-[#F5F7FB]" aria-label="더보기">
            <ReportIcon name="more" />
        </summary>
        <div className="absolute right-0 top-full z-30 mt-[8px] max-h-[65vh] w-[460px] max-w-[80vw] overflow-y-auto rounded-[16px] border border-solid border-[#E5E8EB] bg-white p-[24px] shadow-lg">
            {controls}
            {report && <>
                <div className="mt-[16px] flex flex-wrap items-center gap-[16px]">
                    <button type="button" aria-expanded={showBasis} aria-controls="session-report-basis"
                        onClick={() => setShowBasis((value) => !value)} className="min-h-[40px] text-[14px] leading-[20px] text-[#4E5968]">
                        {showBasis ? '계산한 시각·범위 접기' : '계산한 시각·범위 보기'} <span aria-hidden="true">ⓘ</span>
                    </button>
                    <ExplanationToggle />
                </div>
                {showBasis && <div id="session-report-basis" className="mt-[16px] space-y-[8px] border-t border-solid border-[#E5E8EB] pt-[16px] text-[14px] leading-[20px] text-[#4E5968]">
                    <p>{`강사 ${report.coaches.length}명, 회원 목록 ${formatReportNumber(totalRows)}건이에요. 개인 수업 수강권만 세고 그룹 수업은 빼요.`}</p>
                    <p>{`${formatKst(report.generatedAt, 'M월 D일 HH:mm')} 기록으로 셌어요.`}</p>
                    <p>전체 요약과 강사별 회원 목록을 한 엑셀 파일로 받아요.</p>
                </div>}
            </>}
        </div>
    </details>;
};

/** 기준 시각과 경고를 한 줄에 두고 자세한 안내만 펼친다. */
export const SessionReportWarnings = ({ report, isNoticeOpen, onToggleNotice, isCurrentMonth = false, onPrevMonth, onRecalculate, isRecalculating }: {
    report: SessionReport; isNoticeOpen: boolean; onToggleNotice: () => void;
    isCurrentMonth?: boolean; onPrevMonth?: () => void; onRecalculate?: () => void; isRecalculating?: boolean;
}) => {
    const [details, setDetails] = useState(false);
    const notices = describeNotices(report.notices.filter((notice) => !PROMINENT_NOTICE_CODES.includes(notice.code)));
    const prominentNotices = describeProminentNotices(report.notices.filter((notice) => notice.code !== 'PAYMENT_LOOKUP_INCOMPLETE'));
    const refundIncomplete = report.notices.some((notice) => notice.code === 'PAYMENT_LOOKUP_INCOMPLETE' && (notice.count ?? notice.sessions ?? 0) > 0);
    const month = Number(report.yearMonth.split('-')[1]);
    return <div className="mt-[8px] text-[13px] leading-[22px] text-[#4E5968]">
        <div aria-label="계산 시점과 안내" className="flex min-h-[40px] flex-wrap items-center gap-x-[14px] gap-y-[4px]">
            {!report.isMidMonth && <span>{formatKst(report.generatedAt, 'M월 D일 HH:mm')} 기준</span>}
            <button type="button" aria-expanded={isNoticeOpen} aria-controls="session-report-notices" onClick={onToggleNotice} className="inline-flex items-center gap-[4px] text-[#2B5DB3]">
                {isNoticeOpen ? '▾' : '▸'} 계산 기준·안내
            </button>
            {refundIncomplete && <span className="inline-flex flex-wrap items-center gap-[6px] text-[#9A4B08]">
                <span aria-hidden="true" className="h-[6px] w-[6px] rounded-full bg-[#D97706]" />환불 숫자가 실제보다 적을 수 있어요 ·
                <button type="button" disabled={isRecalculating} onClick={onRecalculate} className="font-medium disabled:opacity-50">다시 계산</button>
            </span>}
            {prominentNotices.map((text) => <span key={text} role="note" className="text-[#9A4B08]">{screenReportText(text)}</span>)}
        </div>
        {isNoticeOpen && <div id="session-report-notices" className="mt-[4px] space-y-[10px] rounded-[12px] border border-solid border-[#E5E8EB] bg-white p-[20px] text-[14px]">
            <h3 className="font-semibold text-[#333D4B]">꼭 알아 둘 것 2개</h3>
            <p>지난달 숫자도 지금 남아 있는 기록으로 다시 계산해요. 다시 계산하면 달라질 수 있으니 매달 초 엑셀을 보관해 주세요.</p>
            <p>미수금은 오늘 기준이에요. 지난달을 선택해도 오늘 남은 미수금을 보여 드려요.</p>
            <button type="button" aria-expanded={details} onClick={() => setDetails((value) => !value)} className="text-[#2B5DB3]">{details ? '▾ 자세히 접기' : '▸ 자세히'}</button>
            {details && <div className="space-y-[10px] border-t border-solid border-[#E9EDF2] pt-[12px]">
                {report.isMidMonth && <p>{isCurrentMonth ? '아직 진행 중인 달이에요.' : '이 달은 중간에 계산했어요. 다시 계산하면 그 달 전체 기록을 볼 수 있어요.'}
                    {isCurrentMonth && onPrevMonth && <button type="button" onClick={onPrevMonth} className="ml-[8px] text-[#2B5DB3]">{`급여 정산용이면 ${month === 1 ? 12 : month - 1}월 보기`} ›</button>}
                </p>}
                {notices.map((text) => <p key={text} className="break-keep">{screenReportText(text)}</p>)}
                <p>{formatKst(report.generatedAt)}에 계산했어요. 월말 뒤에 입력하거나 취소한 기록은 {getNoticeCount(report.notices, 'CHANGED_AFTER_MONTH_END')}건이에요.</p>
                <p>회차 추가 결제는 따로 표시해요. 환불은 회차가 줄어든 수강권의 현재 담당 강사에게 반영해요. 삭제된 결제는 계산에서 뺐어요.</p>
                <p>결제 없이 직접 고친 회차는 지난달 남은 회차에 들어 있어요. 별도 항목으로는 보이지 않아요.</p>
            </div>}
        </div>}
    </div>;
};
